import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AlertComponent,
	ButtonComponent,
	CreateLayoutComponent,
	FooterActionsComponent,
	IToogleSelect,
	ItemComponent,
	ListDetailComponent,
	TagComponent,
} from '@tecalisdev/infinity';
import { LoadingService } from '../../../../../shared/services/loading.service';
import { MasterService } from '../../../../../shared/services/master.service';
import { SnackbarService } from '../../../../../shared/services/snackbar.service';
import { DesignThemeDetailComponent } from '../../components/design-theme-detail/design-theme-detail.component';
import { ModalCancelDesignComponent } from '../../components/modal-cancel-design/modal-cancel-design.component';
import {
	IThemeMode,
	ModalDesignThemeComponent,
} from '../../components/modal-design-theme/modal-design-theme.component';
import { DesignHelperService } from '../../services/design-helper.service';
import { DesignService } from '../../services/design.service';

/** Estado del router: datos del modal abierto desde el listado (NIF-750). */
export const DESIGN_THEME_FROM_LIST_STATE_KEY = 'themeFromListModal';

@Component({
	selector: 'app-design-form',
	imports: [
		ReactiveFormsModule,
		TranslatePipe,
		CreateLayoutComponent,
		AlertComponent,
		FooterActionsComponent,
		ButtonComponent,
		ItemComponent,
		TagComponent,
		ListDetailComponent,
		DesignThemeDetailComponent,
		ModalCancelDesignComponent,
		ModalDesignThemeComponent,
	],
	templateUrl: './design-form.component.html',
	styleUrl: './design-form.component.scss',
})
export class DesignFormComponent implements OnInit {
	public designHelper = inject(DesignHelperService);
	private designService = inject(DesignService);
	private masterService = inject(MasterService);
	private router = inject(Router);
	private snackbar = inject(SnackbarService);
	private loading = inject(LoadingService);
	private trans = inject(TranslateService);

	public form: FormGroup = this.designHelper.form;
	public showError: string = '';
	public showModalDesignTheme: boolean = false;
	public showModalCancelDesign: boolean = false;
	public selectedSkin: 'light' | 'dark' = 'light';

	get companyLabel(): string {
		const uuid = this.form.get('company_uuid')?.value;
		if (!uuid) return '';
		const id = typeof uuid === 'object' && uuid !== null && 'id' in uuid ? uuid.id : uuid;
		const company = this.masterService.companies?.find((c) => c.id === id);
		return (company?.label as string) || '';
	}

	get currentThemeControl(): FormControl | null {
		const currentControl = this.form.get(this.selectedSkin);
		return currentControl instanceof FormControl ? currentControl : null;
	}

	get hasThemeEditor(): boolean {
		return !!this.form.value.title?.trim() && !!this.currentThemeControl;
	}

	ngOnInit(): void {
		const themeFromList = this.consumeThemeFromListState();
		if (themeFromList?.title?.length) {
			this.applyThemeFromModal(themeFromList);
			return;
		}
		// Deep link a /create sin pasar por el listado: mantener modal como fallback
		this.openModalDesignTheme();
	}

	/** Lee y limpia el state del router para no reaplicar al refrescar. */
	private consumeThemeFromListState(): IThemeMode | null {
		const navState =
			this.router.getCurrentNavigation()?.extras?.state ??
			(typeof history !== 'undefined' ? history.state : null);
		const theme = navState?.[DESIGN_THEME_FROM_LIST_STATE_KEY] as IThemeMode | undefined;
		if (!theme) return null;
		if (typeof history !== 'undefined' && history.state?.[DESIGN_THEME_FROM_LIST_STATE_KEY]) {
			const { [DESIGN_THEME_FROM_LIST_STATE_KEY]: _removed, ...rest } = history.state;
			history.replaceState(rest, '');
		}
		return theme;
	}

	public cancel(): void {
		if (this.form.dirty) {
			this.showModalCancelDesign = true;
			return;
		}
		this.router.navigate(['/settings/design']);
	}

	public openModalDesignTheme(): void {
		this.showModalDesignTheme = true;
	}

	public closeModalDesignTheme(event?: IThemeMode | null): void {
		this.showModalDesignTheme = false;
		if (event && Object.keys(event).length > 0 && event?.title?.length > 0) {
			this.applyThemeFromModal(event);
		} else {
			this.cancel();
		}
	}

	/** Aplica nombre, compañía y skin desde el modal de tema (listado o create). */
	private applyThemeFromModal(event: IThemeMode): void {
		this.form.patchValue({ title: event.title || '' });
		const selectedMode = this.resolveSkinFromModeAvailable(event.modeAvailable);
		this.selectedSkin = selectedMode;

		if (event.modeAvailable && Array.isArray(event.modeAvailable) && event.modeAvailable.length > 0) {
			const hasLightMode = event.modeAvailable.some((mode: IToogleSelect) => mode.id === 'light');
			if (!hasLightMode) {
				this.form.removeControl('light');
				return;
			}

			const hasDarkMode = event.modeAvailable.some((mode: IToogleSelect) => mode.id === 'dark');
			if (!hasDarkMode) {
				this.form.removeControl('dark');
				return;
			}
		} else {
			if (selectedMode === 'light') {
				this.form.removeControl('dark');
			} else if (selectedMode === 'dark') {
				this.form.removeControl('light');
			}
		}

		if (event.company_uuid) {
			this.form.get('company_uuid')?.setValue(event.company_uuid);
		}
	}

	public closeModalCancelDesign(action: boolean | null): void {
		this.showModalCancelDesign = false;
		if (action === null) return;
		if (action) {
			this.save(false, true);
			return;
		}
		this.router.navigate(['/settings/design']);
	}

	public save(isDraft: boolean = false, navigateAfterSave: boolean = false): void {
		if (!isDraft && this.form.invalid) {
			this.form.markAllAsTouched();
			this.showError = this.trans.instant('DESIGN.FORM.ERROR_GENERAL');
			return;
		}

		this.loading.show(this.trans.instant('LOADING.CREATE_DESIGN'));
		const body = this.getBody();

		this.designService.create(body).subscribe({
			next: () => {
				this.loading.hide();
				this.snackbar.show(this.trans.instant('DESIGN.FORM.SAVE_SUCCESS'));
				if (navigateAfterSave) {
					this.router.navigate(['/settings/design']);
					return;
				}
				this.router.navigate(['/settings/design']);
			},
			error: (error) => {
				this.loading.hide();
				this.showError = error.message || this.trans.instant('DESIGN.FORM.SAVE_ERROR');
			},
		});
	}

	private getBody(): FormData {
		const formValue = this.form.value;
		const skin = this.form.get('light') ? 'light' : this.form.get('dark') ? 'dark' : this.selectedSkin;
		const theme = skin === 'light' ? formValue.light : formValue.dark;
		const formData = new FormData();

		formData.append('title', formValue.title);
		formData.append('active', formValue.active ? '1' : '0');
		formData.append('company_uuid', formValue.company_uuid || '');
		formData.append('show_product_name', theme?.show_product_name ? '1' : '0');
		this.designHelper.appendThemeAssetToFormData(formData, 'custom_spinner', theme?.custom_spinner);
		this.designHelper.appendThemeAssetToFormData(formData, 'logo', theme?.logo);
		this.designHelper.appendThemeAssetToFormData(formData, 'favicon', theme?.favicon);

		formData.append('watermark', theme?.watermark ? '1' : '0');

		if (theme?.css_configs && Array.isArray(theme?.css_configs)) {
			formData.append('css_configs', JSON.stringify(theme.css_configs));
		}

		formData.append('skin', skin);

		return formData;
	}

	private resolveSkinFromModeAvailable(modeAvailable: any): 'light' | 'dark' {
		if (Array.isArray(modeAvailable) && modeAvailable.length > 0) {
			return modeAvailable.some((mode: IToogleSelect) => mode.id === 'light') ? 'light' : 'dark';
		}

		if (typeof modeAvailable === 'boolean') {
			return modeAvailable ? 'light' : 'dark';
		}

		if (modeAvailable && typeof modeAvailable === 'object') {
			if ('id' in modeAvailable && modeAvailable.id) {
				return modeAvailable.id === 'light' ? 'light' : 'dark';
			}

			if ('value' in modeAvailable) {
				return modeAvailable.value ? 'light' : 'dark';
			}
		}

		if (modeAvailable === 'light' || modeAvailable === 'dark') {
			return modeAvailable;
		}

		return 'light';
	}
}
